import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'

import { environment } from '../../environments/environment'
import {
  QueueOverview,
  QueueStats,
  FailedJobsResponse,
  RecurringTask
} from '../_models/solid-queue.interface'

// Service für die Solid Queue API-Kommunikation
@Injectable({
  providedIn: 'root'
})
export class SolidQueueService {

  private basePath = environment.apiURL + 'solid_queue'

  constructor(
    private http: HttpClient,
  ) {
  }

  // Übersichtsdaten laden und in Frontend-Format umwandeln
  public getOverview(): Observable<QueueOverview> {
    return this.http.get<any>(`${this.basePath}/overview`).pipe(
      map(data => {
        // Queues von Hash in Array umwandeln und paused-Status hinzufügen
        const pausedQueues: string[] = data.paused_queues || []
        const queues: QueueStats[] = Object.keys(data.queues).map(name => ({
          name,
          ready: data.queues[name].ready,
          scheduled: data.queues[name].scheduled,
          blocked: data.queues[name].blocked,
          failed: data.queues[name].failed,
          paused: pausedQueues.includes(name)
        }))

        return {
          queues,
          total_jobs: data.totals.jobs,
          finished_jobs: data.totals.finished,
          processes: data.processes
        }
      })
    )
  }

  // Fehlgeschlagene Jobs mit Paginierung laden
  public getFailedJobs(page: number = 1, perPage: number = 25): Observable<FailedJobsResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', perPage.toString())
    return this.http.get<any>(`${this.basePath}/failed_jobs`, { params }).pipe(
      map(data => ({
        jobs: data.failed_jobs,
        total: data.pagination.total_count,
        page: data.pagination.page,
        per_page: data.pagination.per_page,
        total_pages: data.pagination.total_pages
      }))
    )
  }

  // Wiederkehrende Aufgaben laden
  public getRecurringTasks(): Observable<RecurringTask[]> {
    return this.http.get<any>(`${this.basePath}/recurring_tasks`).pipe(
      map(data => data.recurring_tasks.map((task: any) => ({
        ...task,
        last_run_at: task.last_execution?.run_at || null
      })))
    )
  }

  // Einzelnen fehlgeschlagenen Job erneut ausführen
  public retryJob(id: number): Observable<any> {
    return this.http.post(`${this.basePath}/retry_job/${id}`, {})
  }

  // Alle fehlgeschlagenen Jobs erneut ausführen
  public retryAllFailed(): Observable<any> {
    return this.http.post(`${this.basePath}/retry_all_failed`, {})
  }

  // Einzelnen fehlgeschlagenen Job verwerfen
  public discardJob(id: number): Observable<any> {
    return this.http.delete(`${this.basePath}/discard_job/${id}`)
  }

  // Alle fehlgeschlagenen Jobs verwerfen
  public discardAllFailed(): Observable<any> {
    return this.http.delete(`${this.basePath}/discard_all_failed`)
  }

  // Queue pausieren
  public pauseQueue(queueName: string): Observable<any> {
    return this.http.post(`${this.basePath}/pause_queue`, { queue_name: queueName })
  }

  // Queue fortsetzen
  public resumeQueue(queueName: string): Observable<any> {
    return this.http.post(`${this.basePath}/resume_queue`, { queue_name: queueName })
  }

  // Wiederkehrende Aufgabe manuell auslösen
  public triggerRecurring(key: string): Observable<any> {
    return this.http.post(`${this.basePath}/trigger_recurring/${key}`, {})
  }

  // Fertige Jobs aufräumen
  public clearFinished(): Observable<any> {
    return this.http.delete(`${this.basePath}/clear_finished`)
  }
}
